import {useEffect, useRef, useState} from 'react';
import {
  MAIN_LIST_CNT,
  DIRECTORY_CONTENT_INIT_LIST,
  DIRECTORY_LIVE_CONTEXT_MENU,
  DIRECTORY_VOD_CONTEXT_MENU,
  DIRECTORY_LIST_CNT,
} from '@/constants/main';
import CategoryApi from '@/api/directory/category';
import {ListTab} from '@/components/main/directory/common/ListTab';
import Catch from '@/components/my/common/item/MyCatch';
import {isLogin} from '@/utils/main';
import {useNavigate} from 'react-router-dom';
import useDirectoryStore from '@/stores/directory';
import LogApi from '@/api/common/log';
import {useHiddenStreamer} from '../../common/hooks/useHiddenStreamer';
import {BroadcastLayout, BroadcastType} from '../../common/Broadcast';
import ErrorBoundary from './ErrorBoundary';
import {SoopLoadingSpinner} from '@/components/ui';
import useGlobalUIStore from '@/stores';
import {AUTH_TYPES, BROADCAST_TYPES} from '@/constants';

/**
 * [탐색 -> 카테고리 -> 카테고리 아이템 선택] 시 표시되는 영역
 * @param {Object} params
 * @param {String} params.categoryName 선택한 카테고리 이름
 * @param {Object} params.categoryInfo 선택한 카테고리 상세 정보
 * @param {String} params.categoryInfo.cateImg 카테고리 프로필 이미지
 * @param {String} params.categoryInfo.categoryName 카테고리 이름
 * @param {Object.<string, boolean>} params.categoryInfo.contentsDisplay 각 탭(live, vod, catch) 표시 여부
 * @param {Array} params.categoryInfo.fixedTags 카테고리 기본 설정 태그
 * @param {Number} params.categoryInfo.viewCnt 시청자 수
 * @param {String} params.categoryInfo.liveCategoryNo 라이브 카테고리 ID
 * @param {String} params.categoryInfo.vodCategoryNo VOD 카테고리 ID
 * @param {String} params.categoryInfo.liveContents 라이브 목록 요청 API
 * @param {String} params.categoryInfo.vodContents VOD 목록 요청 API
 * @param {String} params.categoryInfo.catchContents 캐치 목록 요청 API
 * @param {String} params.categoryType 카테고리 내부에서 선택된 탭 정보
 * @returns
 */
const CategoryContent = ({categoryName, categoryInfo, categoryType}) => {
  const {
    selectedCategoryTab,
    selectedCategoryInfo,
    selectedContentFilter,
    selectedPeriodFilter,
    setSelectedCategoryTab,
    setSelectedContentFilterByTab,
    setSelectedPeriodFilter,
  } = useDirectoryStore();

  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState(
    selectedContentFilter['live']?.[0]?.type || 'view_cnt_desc',
  );
  const [isLoading, setIsLoading] = useState(false);
  const [categoryContentList, setCategoryContentList] = useState(
    DIRECTORY_CONTENT_INIT_LIST,
  );
  const {
    remainCount,
    slicedBroadList,
    setRemainCount,
    setSlicedBroadList,
    filterBroadList,
  } = useHiddenStreamer();
  const [hiddenStreamer, setHiddenStreamer] = useState([]);
  const {refreshTimestamp, setRefreshTimestamp} = useGlobalUIStore();

  /** 현재 카테고리 정보 상태 변경에 반응 */
  useEffect(() => {
    if (!categoryInfo || Object.keys(categoryInfo).length === 0) {
      return;
    }
    const {contentsDisplay} = categoryInfo;

    /**
     * 카테고리 정보 변경 시 노출 우선순위에 따라 탭 자동 전환 함수
     * 특정 카테고리의 경우 일부 탭이 존재하지 않을 가능성이 있어 자동으로 우선순위에 따라 전환해주는 함수를 사용
     * ex. JTBC 카테고리는 LIVE 탭만 존재, "/directory/category/JTBC/vod"로 접근시 자동으로 "/live"로 navigation
     **/
    const handleTabSelection = (currentTab, fallbackTab1, fallbackTab2) => {
      const navigateToTab = (tab) => {
        setSelectedCategoryTab(tab);
        navigate(
          `/directory/category/${encodeURIComponent(categoryName)}/${tab}`,
          {
            replace: true, // 자동 변환에 의한 이동이기 때문에 히스토리 남기지 않음
          },
        );
      };

      if (!contentsDisplay[currentTab]) {
        if (contentsDisplay[fallbackTab1]) {
          navigateToTab(fallbackTab1);
        } else {
          navigateToTab(fallbackTab2);
        }
      } else {
        setSelectedCategoryTab(currentTab);
      }
    };

    // 노출 우선순위 별로 선택된 탭을 조정
    switch (categoryType) {
      case 'live':
        handleTabSelection('live', 'vod', 'catch');
        break;
      case 'vod':
        handleTabSelection('vod', 'live', 'catch');
        break;
      case 'catch':
        handleTabSelection('catch', 'live', 'vod');
        break;
      default:
        break;
    }
  }, [categoryInfo]);

  /**
   * [페이지, 정렬 정보, 필터 정보, 선택된 카테고리 탭, 새로고침 시간]에 반응한다.
   * 방송/영상 목록을 업데이트한다.
   */
  useEffect(() => {
    if (page === 1) {
      setIsLoading(true);
    }
    if (categoryContentList.isMore) {
      if (isLogin()) {
        getHiddenStreamerList();
      } else {
        getCategoryContents();
      }
    } else {
      if (slicedBroadList.length > 0) {
        setCategoryContentList({
          ...categoryContentList,
          list: [...categoryContentList.list, ...slicedBroadList],
        });
        setSlicedBroadList([]);
      } else {
        if (isLogin()) {
          getHiddenStreamerList();
        } else {
          getCategoryContents();
        }
      }
    }
  }, [
    page,
    order,
    selectedCategoryTab,
    refreshTimestamp,
    selectedContentFilter,
    selectedPeriodFilter,
  ]);

  useEffect(() => {
    if (hiddenStreamer.length > 0) {
      getCategoryContents();
    }
  }, [hiddenStreamer]);

  useEffect(() => {
    if (remainCount > 0) {
      setPage((prev) => prev + 1);
    }
  }, [remainCount]);

  /** 탭 변경시 리스트 상태 초기화 */
  useEffect(() => {
    setPage(1);
    setRemainCount(0);
    setSlicedBroadList([]);
    setCategoryContentList(DIRECTORY_CONTENT_INIT_LIST);
  }, [categoryType]);

  /** 로그 전송 처리 */
  const logViewEvent = (response) => {
    if (response?.list?.length > 0) {
      if (selectedCategoryTab === 'live') {
        handleLiveViewLog(response);
      } else if (['vod', 'catch'].includes(selectedCategoryTab)) {
        handleVodViewLog(response);
      }
    }
  };

  const handleLiveViewLog = (response) => {
    try {
      const params = {
        path1: 'directory',
        path2: 'cate',
        listSendType: 'client',
        listViewCnt: response.list.length,
        listViewData: response.list.map((item) => ({
          bno: item.broadNo,
        })),
      };
      LogApi.sendLiveListViewLog(params);
    } catch (error) {}
  };

  const handleVodViewLog = (response) => {
    try {
      const params = {
        path1: 'directory',
        path2: 'cate',
        listSendType: 'client',
        listViewCnt: response.list.length,
        listViewData: response.list.map((item) => ({
          vno: item.titleNo,
        })),
      };
      LogApi.sendVodListViewLog(params);
    } catch (error) {}
  };

  const refreshTimer = useRef(false);

  /** 탭 클릭 시 동작 */
  const handleOnClickTab = (type) => {
    if (type === selectedCategoryTab) {
      if (refreshTimer.current) {
        return;
      }
      setRefreshTimestamp();
      refreshTimer.current = true;
      setTimeout(() => {
        refreshTimer.current = false;
      }, 1500);
    }
    navigate(
      `/directory/category/${encodeURIComponent(categoryName)}/${type}`,
      {
        replace: type === selectedCategoryTab,
      },
    );
    setSelectedCategoryTab(type);
    setPage(1);

    setRemainCount(0);
    setSlicedBroadList([]);
    setSelectedPeriodFilter({
      key: 'period',
      label: '1주',
      type: '1week',
      applyStatus: false,
      startDate: '',
      endDate: '',
    });
    setCategoryContentList(DIRECTORY_CONTENT_INIT_LIST);
  };

  const handleOnClickMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  /** LIVE 탭 필터 선택 아이템 변경 시 동작 */
  const handleOnClickSort = (type) => {
    if (type === order || (!isLogin() && type === 'prefer')) {
      return;
    }
    setPage(1);
    setOrder(type);
    setSelectedContentFilterByTab('live', [
      {key: 'sort', label: '조회수', type: type},
    ]);
    setRemainCount(0);
    setSlicedBroadList([]);
    setCategoryContentList(DIRECTORY_CONTENT_INIT_LIST);
  };

  /**
   * 아이템 데이터 가져오기 요청에 대한 식별자 기록
   * Note.
   * 느린 네트워크(Slow 4G 정도 수준) 환경에서 VOD 탭 -> LIVE 탭 빠르게 전환 시 LIVE 탭에 VOD 아이템이 들어가는 이슈가 있음
   * 현행 코드 구조상 발송된 요청을 Abort 시킬 수 없기 때문에 요청 식별자를 사용하는 방식으로 제어 (Team FE Notion TSK-2324)
   */
  const latestRequestRef = useRef(0);

  /** 아이템 리스트에 표시할 데이터들을 불러오는 함수 */
  const getCategoryContents = async () => {
    latestRequestRef.current += 1;
    const currentRequest = latestRequestRef.current;

    try {
      const queryParams = createQueryParams();

      if (!isValidCategoryDisplay() || !queryParams) {
        return;
      }
      const response = await CategoryApi.getCategoryContents(queryParams);
      if (currentRequest === latestRequestRef.current && response) {
        // 가장 최근 발송된 요청의 응답만 사용
        handleContentList(response);
        logViewEvent(response);
      }
    } catch (error) {
      setCategoryContentList(DIRECTORY_CONTENT_INIT_LIST);
    } finally {
      setIsLoading(false);
    }
  };

  const getHiddenStreamerList = async () => {
    try {
      const response = await CategoryApi.getHiddenStreamerList();
      if (response && response.result === 1) {
        if (response.data.length === 0) {
          getCategoryContents();
        } else {
          setHiddenStreamer([...response.data]);
        }
      }
    } catch (error) {
      getCategoryContents();
    }
  };

  /** VOD/Catch 탭에서 필터 클릭 시 동작 */
  const handleVodFilterClick = (key, label, type) => {
    if (key === 'period') {
      setSelectedPeriodFilter({
        ...selectedPeriodFilter,
        label,
        type,
        applyStatus: false,
      });
    } else {
      setSelectedContentFilterByTab(
        selectedCategoryTab,
        selectedContentFilter[selectedCategoryTab].map((filter) =>
          filter.key === key ? {...filter, label, type} : filter,
        ),
      );
    }

    setPage(1);
    setRemainCount(0);
    setSlicedBroadList([]);
    //직접 입력상태일때는 실제로 날짜 set 시키기 전에는 리스트 초기화 시키지 말라고 정책 내용 반영 (직접입력 탭을 누른 상태이면 유저의 액션의 의해서 탭 이동이니 초기화가 맞는거같은데 이해가 안되긴 함)
    if (type !== 'directInput') {
      setCategoryContentList(DIRECTORY_CONTENT_INIT_LIST);
    }
  };

  // Response 데이터 처리
  const handleContentList = (response) => {
    const {isMore, list} = response;
    if (hiddenStreamer.length > 0) {
      filterBroadList(
        selectedCategoryTab,
        page,
        list,
        isMore,
        hiddenStreamer,
        setCategoryContentList,
      );
    } else {
      setCategoryContentList((prevState) => ({
        isMore: isMore,
        list: page > 1 ? [...prevState.list, ...list] : [...list],
      }));
    }
  };

  /** 현재 컴포넌트의 상태를 조합해 쿼리 파라미터를 생성하는 함수 */
  const createQueryParams = () => {
    const baseParams = {
      m: 'categoryContentsList',
      szType: selectedCategoryTab,
      nPageNo: page,
      nListCnt:
        selectedCategoryTab === 'live' ? MAIN_LIST_CNT : DIRECTORY_LIST_CNT,
      szPlatform: 'pc',
    };

    //categoryNo === '00000000' 으로 올때는 해당탭 비노출에서 => contentsDisplay 의 값들로 비노출 여부판단으로 바뀜
    if (selectedCategoryTab === 'live') {
      baseParams.szOrder = order;
      baseParams.szCateNo = categoryInfo.liveCategoryNo;
    } else {
      baseParams.szOrder = selectedContentFilter[selectedCategoryTab].find(
        (item) => item.key === 'sort',
      )?.type;
      baseParams.szTerm = selectedPeriodFilter.type;

      if (selectedCategoryTab === 'vod') {
        baseParams.szFileType = selectedContentFilter[selectedCategoryTab].find(
          (item) => item.key === 'fileType',
        )?.type;
      }
      if (baseParams.szTerm === 'directInput') {
        const {startDate, endDate, applyStatus} = selectedPeriodFilter;
        baseParams.szStartDate = startDate;
        baseParams.szEndDate = endDate;
        if (!applyStatus) {
          return;
        }
      }
      baseParams.szCateNo = categoryInfo.vodCategoryNo;
    }
    return baseParams;
  };

  /** 카테고리별 컨텐츠 표시 여부 확인 */
  const isValidCategoryDisplay = () => {
    const displaySettings = selectedCategoryInfo.contentsDisplay;
    if (selectedCategoryTab === 'live') {
      return displaySettings.live;
    } else if (selectedCategoryTab === 'vod') {
      return displaySettings.vod;
    } else if (selectedCategoryTab === 'catch') {
      return displaySettings.catch;
    }
    return false;
  };

  const listTabProps = {
    order,
    categoryInfo,
    handleOnClickTab,
    handleOnClickSort,
    handleVodFilterClick,
  };

  return (
    <>
      <ListTab {...listTabProps} />
      {isLoading ? (
        <SoopLoadingSpinner />
      ) : (selectedCategoryTab === 'live' || selectedCategoryTab === 'vod') &&
        categoryContentList?.list?.length > 0 ? (
        <div className='cBox-list'>
          <ul>
            {categoryContentList?.list?.map((broadcast, broadcastIdx) => (
              <li
                data-type='cBox'
                key={`${broadcast.userId || broadcast.broadcastId}-${broadcastIdx}`}>
                {selectedCategoryTab === 'live' && (
                  <ErrorBoundary fallback={<div>로딩중</div>}>
                    <BroadcastLayout
                      broadcastType={BROADCAST_TYPES.LIVE}
                      liveType={broadcast.broadType}
                      broadcastId={broadcast.broadNo}
                      categoryNo={broadcast.broadCateNo}
                      categoryName={broadcast.categoryName}
                      categoryTags={broadcast.categoryTags}
                      autoHashTags={broadcast.autoHashtags}
                      hashTags={broadcast.hashTags}
                      canResend={Boolean(Number(broadcast.visitBroadType))}
                      allowsAdult={Number(broadcast.grade) === 19}
                      hasPassword={Boolean(broadcast.isPassword)}
                      hasDrops={Boolean(broadcast.isDrops)}
                      thumbnailUrl={broadcast.thumbnail}
                      title={broadcast.broadTitle}
                      userId={broadcast.userId}
                      userNickname={broadcast.userNick}
                      viewCount={broadcast.viewCnt}
                      startDateTime={broadcast.broadStart}
                      subscriptionOnly={broadcast.subscriptionOnly}
                      contextMenu={{
                        menuList: DIRECTORY_LIVE_CONTEXT_MENU,
                      }}
                      log={{
                        data: {
                          directoryCateId: categoryInfo.categoryName,
                          categoryTags: categoryInfo.fixedTags,
                        },
                      }}>
                      <BroadcastType.Live />
                    </BroadcastLayout>
                  </ErrorBoundary>
                )}
                {selectedCategoryTab === 'vod' && (
                  <ErrorBoundary fallback={<div>로딩중</div>}>
                    <BroadcastLayout
                      broadcastType={BROADCAST_TYPES.VOD}
                      broadcastId={broadcast.titleNo}
                      authNumber={AUTH_TYPES[broadcast.auth]}
                      vodType={broadcast.fileType}
                      duration={broadcast.duration}
                      categoryName={
                        broadcast.categoryTags?.length > 0
                          ? broadcast.categoryTags[0]
                          : ''
                      }
                      autoHashTags={broadcast.autoHashtags}
                      categoryTags={broadcast.categoryTags}
                      categoryNo={broadcast.category}
                      vodCategoryNo={broadcast.vodCategory}
                      accessibleAge={Number(broadcast.grade)}
                      allowsAdult={
                        Number(broadcast.grade) === 19 &&
                        Number(broadcast.category) === 30000
                      }
                      hashTags={broadcast.hashTags}
                      hasBadge={true}
                      thumbnailUrl={broadcast.thumbnail}
                      title={broadcast.title}
                      isPpv={broadcast.ppv}
                      userId={broadcast.userId}
                      userNickname={broadcast.userNick}
                      originalUserId={broadcast.originalUserId}
                      originalUserNickname={broadcast.originalUserNick}
                      viewCount={broadcast.viewCnt}
                      createdDate={broadcast.regDate}
                      previewThumbnailUrl={broadcast.webpPath}
                      contextMenu={{
                        menuList: DIRECTORY_VOD_CONTEXT_MENU,
                      }}
                      log={{
                        data: {
                          directoryCateId: categoryInfo.categoryName,
                          ...(selectedCategoryTab === 'vod' && {
                            exCategoryNo: categoryInfo.vodCategoryNo,
                          }),
                          categoryTags: categoryInfo.fixedTags,
                        },
                      }}>
                      <BroadcastType.Vod />
                    </BroadcastLayout>
                  </ErrorBoundary>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : selectedCategoryTab === 'catch' &&
        categoryContentList?.list?.length > 0 ? (
        <div className='catch_area'>
          <div className='catch_list'>
            <ul>
              {categoryContentList?.list?.map((catchItem, catchIdx) => (
                <li key={`${catchItem.catchId}-${catchIdx}`}>
                  <Catch
                    catchId={catchItem.titleNo}
                    thumbnailSrc={catchItem.thumbnail}
                    isAdult={Number(catchItem.grade) === 19}
                    category={catchItem.category}
                    vodCategory={catchItem.vodCategory}
                    isHorizontal={catchItem.resolutionType === 'horizontal'}
                    viewCount={catchItem.viewCnt}
                    {...catchItem}
                    isLogSendData={{
                      directoryCateId: categoryInfo.categoryName,
                      exCategoryNo: categoryInfo.vodCategoryNo,
                    }}
                    catchContextMenuList={[{label: '공유하기', type: 'share'}]}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        categoryContentList?.list?.length === 0 && (
          <div
            className={`noList ${selectedCategoryTab === 'live' ? 'live' : ''}`}>
            <strong>
              해당 카테고리의{' '}
              {selectedCategoryTab === 'catch'
                ? 'Catch'
                : selectedCategoryTab.toUpperCase()}
              가 없습니다.
            </strong>
          </div>
        )
      )}
      {(slicedBroadList.length > 0 || categoryContentList?.isMore) && (
        <div className='show_more'>
          <button type='button' onClick={handleOnClickMore}>
            더보기
          </button>
        </div>
      )}
    </>
  );
};
export default CategoryContent;
